/**
 * A look-ahead peak limiter, offline.
 *
 * Its one job is to let a loudness target be reached on material whose peaks
 * would otherwise stand in the way. Without it, asking for -14 LUFS on anything
 * dynamic can only be refused; with it, the few moments that poke through the
 * ceiling are turned down and the rest is left alone.
 *
 * How it works, in the order the signal meets it:
 *
 *  1. **Detection on inter-sample peaks.** A converter reconstructs a curve
 *     through the samples, and that curve can rise above all of them. Limiting to
 *     the sample peak therefore leaves real overshoots behind, so detection runs
 *     on a four-times oversampled estimate — the same one the loudness meter
 *     reports as true peak.
 *  2. **A required-gain curve.** For each sample, the gain that would bring it to
 *     the ceiling, or 1 where it is already under.
 *  3. **A sliding minimum over the look-ahead.** This is what makes it a
 *     look-ahead limiter rather than a clipper: the gain starts coming down
 *     *before* the peak arrives, so nothing is caught by surprise and no edge is
 *     squared off.
 *  4. **Smoothing.** The fall is shaped over the look-ahead window and the
 *     recovery over a release time, both with a one-pole. An abrupt gain change
 *     is a click, and a click is louder than whatever it was avoiding.
 *  5. **One gain for every channel.** Reducing channels independently would move
 *     the image whenever one side is louder, which is a strange thing to hear.
 *
 * There is no distortion stage, no soft knee and no oversampled clipping: this
 * is a transparent safety limiter, not a loudness weapon. Pushed too hard it will
 * pump rather than pretend.
 */

export interface LimiterOptions {
  /** Highest true peak allowed, in dB relative to full scale. */
  ceilingDb?: number;
  /** How far ahead the limiter sees. Longer is gentler and less precise in time. */
  lookaheadMs?: number;
  /** How quickly gain returns after a peak has passed. */
  releaseMs?: number;
}

import { interSamplePeaks } from './loudness';

export { interSamplePeaks };

export interface LimiterResult {
  /** The largest reduction applied anywhere, in dB. Zero when nothing was done. */
  maxGainReductionDb: number;
  /** Fraction of samples where any reduction was applied at all, 0 to 1. */
  activeFraction: number;
}

const DEFAULT_CEILING_DB = -1;
const DEFAULT_LOOKAHEAD_MS = 5;
const DEFAULT_RELEASE_MS = 80;

/**
 * Limits `channels` in place so nothing exceeds the ceiling, and reports how
 * much work it had to do.
 */
export function limitPeaks(
  channels: Float32Array[],
  sampleRate: number,
  options: LimiterOptions = {},
): LimiterResult {
  const ceiling = Math.pow(10, (options.ceilingDb ?? DEFAULT_CEILING_DB) / 20);
  const length = channels[0]?.length ?? 0;
  if (length === 0 || channels.length === 0) return { maxGainReductionDb: 0, activeFraction: 0 };

  // Two or three passes, not one.
  //
  // An inter-sample peak is a property of a *neighbourhood* of samples, so
  // reducing the gain around it changes where the reconstructed curve goes — by
  // slightly less than the reduction, because the samples on either side were
  // scaled by slightly different amounts. One pass therefore lands a fraction of
  // a decibel high. Running the same smooth envelope again converges quickly,
  // and each further pass is gentler than the last, so nothing is roughened.
  const totalGain = new Float32Array(length).fill(1);
  let applied = false;
  for (let pass = 0; pass < 4; pass++) {
    if (!limitOnce(channels, totalGain, sampleRate, ceiling, options)) break;
    applied = true;
  }
  if (!applied) return { maxGainReductionDb: 0, activeFraction: 0 };

  let lowest = 1;
  let active = 0;
  for (let i = 0; i < length; i++) {
    if (totalGain[i] < 0.9999) active++;
    if (totalGain[i] < lowest) lowest = totalGain[i];
  }
  return {
    maxGainReductionDb: lowest >= 1 ? 0 : -20 * Math.log10(lowest),
    activeFraction: active / length,
  };
}

/**
 * One pass of the limiter. Returns false when nothing needed doing, which is
 * both the "already quiet enough" case and the signal that the loop has
 * converged.
 */
function limitOnce(
  channels: Float32Array[],
  totalGain: Float32Array,
  sampleRate: number,
  ceiling: number,
  options: LimiterOptions,
): boolean {
  const length = channels[0]?.length ?? 0;
  const peaks = interSamplePeaks(channels);

  // Nothing to do: leave the samples untouched rather than running them through
  // arithmetic that can only lose bits.
  let highest = 0;
  for (const p of peaks) if (p > highest) highest = p;
  if (highest <= ceiling) return false;

  const lookahead = Math.max(1, Math.round(((options.lookaheadMs ?? DEFAULT_LOOKAHEAD_MS) * sampleRate) / 1000));
  const releaseSamples = Math.max(1, Math.round(((options.releaseMs ?? DEFAULT_RELEASE_MS) * sampleRate) / 1000));

  // The gain each sample needs on its own.
  const required = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    required[i] = peaks[i] > ceiling ? ceiling / peaks[i] : 1;
  }

  // Sliding minimum over the look-ahead window, computed with a monotonic deque
  // so the whole pass stays linear however long the window is.
  const target = new Float32Array(length);
  const deque = new Int32Array(length + 1);
  let head = 0;
  let tail = 0;
  const push = (index: number): void => {
    // Anything already in the deque that is no smaller can never be the minimum
    // again, because this new value is both smaller and stays in the window
    // longer.
    while (tail > head && required[deque[tail - 1]] >= required[index]) tail--;
    deque[tail++] = index;
  };

  for (let j = 0; j <= Math.min(length - 1, lookahead); j++) push(j);
  for (let i = 0; i < length; i++) {
    const entering = i + lookahead;
    if (i > 0 && entering < length) push(entering);
    while (deque[head] < i) head++;
    target[i] = required[deque[head]];
  }

  // Smoothing. The attack is shaped over the look-ahead so the reduction arrives
  // gradually rather than as a step; the release is slower, because gain coming
  // back faster than the ear expects is what pumping sounds like.
  const attackCoefficient = Math.exp(-1 / Math.max(1, lookahead / 3));
  const releaseCoefficient = Math.exp(-1 / releaseSamples);
  const gain = new Float32Array(length);
  // Start already down, not at unity. A one-pole starting from 1 takes a few
  // hundred samples to arrive, and audio that is loud from its very first sample
  // would sail over the ceiling during exactly that stretch.
  let current = target[0];
  for (let i = 0; i < length; i++) {
    const wanted = target[i];
    const coefficient = wanted < current ? attackCoefficient : releaseCoefficient;
    current = wanted + (current - wanted) * coefficient;
    // The smoothed curve is never allowed above what this sample actually needs.
    // In the middle of a piece this clamp does nothing, because the sliding
    // minimum has already brought the gain down before the peak arrives; it is
    // the guarantee that makes the ceiling a ceiling rather than a target.
    gain[i] = Math.min(current, required[i]);
  }

  for (let i = 0; i < length; i++) {
    totalGain[i] *= gain[i];
    for (const channel of channels) channel[i] *= gain[i];
  }
  return true;
}
